import { LoyaltyServiceRewardType } from 'generated/graphql-gateway';
import {
  IEngineRewardsMap,
  ISanityRewardsMap,
  LoyaltyEngineReward,
  LoyaltyReward,
} from 'state/loyalty/types';

import {
  Reward,
  RewardCategory,
  RewardLocalizedField,
  RewardsMap,
  ServiceReward,
  ServiceRewardCategory,
} from '../types';

export function toBlockContent(value: unknown): { localeRaw: any[] } {
  if (!value) {
    return { localeRaw: [] };
  }
  if (typeof value === 'object' && !Array.isArray(value) && (value as any).localeRaw) {
    const raw = (value as any).localeRaw;
    return { localeRaw: Array.isArray(raw) ? raw : [] };
  }
  if (Array.isArray(value)) {
    return { localeRaw: value };
  }
  if (typeof value === 'string') {
    return { localeRaw: [{ _type: 'block', children: [{ _type: 'span', text: value }] }] };
  }
  return { localeRaw: [] };
}

function toLegacyBlockContent(value: unknown): any[] {
  if (!value) {
    return [];
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    if (Array.isArray(record.localeRaw)) {
      return record.localeRaw;
    }
    if (Array.isArray(record.enRaw)) {
      return record.enRaw;
    }
    if (Array.isArray(record.locale)) {
      return record.locale;
    }
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    return [{ _type: 'block', children: [{ _type: 'span', text: value }] }];
  }
  return [];
}

function toLegacyDescription(value: unknown): { locale: any[] } | undefined {
  const blocks = toLegacyBlockContent(value);
  return blocks.length ? { locale: blocks } : undefined;
}

function toLegacyEnRaw(value: unknown): { enRaw: any[] } | undefined {
  const blocks = toLegacyBlockContent(value);
  return blocks.length ? { enRaw: blocks } : undefined;
}

function toLocaleString(value: unknown): { locale: string } {
  if (!value) {
    return { locale: '' };
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const maybeLocale = (value as any).locale;
    if (typeof maybeLocale === 'string') {
      return { locale: maybeLocale };
    }
    const maybeEn = (value as any).en;
    if (typeof maybeEn === 'string') {
      return { locale: maybeEn };
    }
    const maybeLocaleRaw = toLegacyBlockContent((value as any).localeRaw);
    if (maybeLocaleRaw.length) {
      return {
        locale: maybeLocaleRaw
          .map(block =>
            Array.isArray(block.children)
              ? block.children.map((child: { text?: string }) => child.text ?? '').join('')
              : ''
          )
          .join(' ')
          .trim(),
      };
    }
  }
  if (typeof value === 'string') {
    return { locale: value };
  }
  return { locale: '' };
}

function toRewardCardImage(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value ?? undefined;
  }

  const imageRecord = value as Record<string, unknown>;
  const locale = imageRecord.locale;
  if (!locale || typeof locale !== 'object' || Array.isArray(locale)) {
    return value;
  }

  const localeRecord = locale as Record<string, unknown>;
  return 'app' in localeRecord ? { ...imageRecord, locale: localeRecord.app ?? null } : value;
}

export function mapServiceRewardToReward(reward: ServiceReward): Reward {
  const renderable = (reward.renderable as Record<string, unknown> | null) ?? {};

  return {
    id: reward.id,
    rewardBenefitId: reward.rewardBenefitId ?? undefined,
    // cmsId and sanityId are the same document id; either service field may be omitted.
    cmsId: reward.cmsId ?? reward.sanityId ?? undefined,
    configId: reward.configId ?? undefined,
    loyaltyId: reward.loyaltyId ?? undefined,
    type: reward.type,
    pointCost: reward.pointCost,
    locked: reward.locked ?? undefined,
    redemptionPercentage: reward.redemptionPercentage ?? undefined,
    limitPerOrder: reward.limitPerOrder ?? undefined,
    remainingPointsNeededForRedemption: reward.remainingPointsNeededForRedemption ?? undefined,
    mobileOrderOnly: reward.mobileOrderOnly ?? undefined,
    incentiveNotInMenu: reward.incentiveNotInMenu ?? undefined,
    errors: reward.errors ?? undefined,
    metadata: reward.metadata ?? undefined,
    incentives: reward.incentives ?? undefined,
    vendorConfigs: reward.vendorConfigs ?? undefined,
    rank: reward.rank ?? undefined,
    startDate: reward.startDate ?? undefined,
    endDate: reward.endDate ?? undefined,
    inlineAlertMessage:
      (renderable.inlineAlertMessage as string | null) ?? reward.inlineAlertMessage ?? undefined,
    name: toLocaleString(renderable.name ?? reward.name).locale,
    // Sanity localeBlockContent wrapper; service enrichment copies the same object onto the reward.
    description: (renderable.description ?? reward.description ?? undefined) as
      | RewardLocalizedField
      | undefined,
    moreInfo: (renderable.moreInfo ?? reward.moreInfo ?? undefined) as
      | RewardLocalizedField
      | undefined,
    howToRedeem: (renderable.howToRedeem ?? reward.howToRedeem ?? undefined) as
      | RewardLocalizedField
      | undefined,
    imageDescription: toLocaleString(renderable.imageDescription ?? reward.imageDescription).locale,
    image: toRewardCardImage(renderable.image ?? reward.image ?? undefined),
    localizedImage: renderable.localizedImage ?? reward.localizedImage ?? undefined,
    backgroundImage: renderable.backgroundImage ?? reward.backgroundImage ?? undefined,
  };
}

export interface IMappedServiceRewardCategories {
  categories: RewardCategory[];
  rewards: Reward[];
  rewardsMap: RewardsMap;
}

export function mapServiceRewardCategoriesToRewards(
  serviceCategories: ReadonlyArray<ServiceRewardCategory>
): IMappedServiceRewardCategories {
  const { categories, rewards, rewardsMap } = mapServiceRewardCategories(serviceCategories);
  return { categories, rewards, rewardsMap };
}

type LegacyRewardSource = Pick<ServiceReward, 'name' | 'sanityId'>;

/** Adapts a Reward for existing list and detail UI. `_id`/`sanityId` are the same document id as `cmsId`. */
export function rewardToLoyaltyReward(reward: Reward, source?: LegacyRewardSource): LoyaltyReward {
  const isConfigReward = reward.type === LoyaltyServiceRewardType.CONFIG;
  const sanityId = source?.sanityId ?? undefined;
  const cmsId = reward.cmsId ?? sanityId ?? reward.id;

  return ({
    _id: sanityId ?? reward.cmsId ?? reward.id,
    _type: isConfigReward ? 'configReward' : 'reward',
    __typename: isConfigReward ? 'ConfigReward' : 'Reward',
    cmsId,
    configId: reward.configId,
    loyaltyEngineId: reward.id,
    sanityId,
    loyaltyId: reward.loyaltyId,
    type: reward.type,
    mobileOrderOnly: reward.mobileOrderOnly,
    incentiveNotInMenu: reward.incentiveNotInMenu,
    rewardBenefitId: reward.rewardBenefitId,
    pointCost: reward.pointCost,
    locked: reward.locked,
    redemptionPercentage: reward.redemptionPercentage,
    limitPerOrder: reward.limitPerOrder,
    remainingPointsNeededForRedemption: reward.remainingPointsNeededForRedemption,
    errors: reward.errors,
    metadata: reward.metadata,
    incentives: reward.incentives,
    vendorConfigs: reward.vendorConfigs,
    rank: reward.rank,
    startDate: reward.startDate,
    endDate: reward.endDate,
    inlineAlertMessage: reward.inlineAlertMessage,
    name: toLocaleString(reward.name),
    description: toLegacyDescription(reward.description),
    moreInfo: toLegacyEnRaw(reward.moreInfo),
    howToRedeem: toLegacyEnRaw(reward.howToRedeem),
    imageDescription: toLocaleString(reward.imageDescription),
    image: reward.image,
    localizedImage: reward.localizedImage,
    backgroundImage: reward.backgroundImage,
  } as unknown) as LoyaltyReward;
}

export function rewardToEngineReward(
  reward: Reward,
  source?: LegacyRewardSource
): LoyaltyEngineReward {
  return ({
    __typename: 'LoyaltyReward',
    id: reward.id,
    rewardBenefitId: reward.rewardBenefitId ?? '',
    name: source ? source.name ?? '' : reward.name,
    pointCost: reward.pointCost,
    sanityId: source?.sanityId ?? null,
    locked: reward.locked ?? false,
    redemptionPercentage: reward.redemptionPercentage ?? null,
    limitPerOrder: reward.limitPerOrder ?? null,
    remainingPointsNeededForRedemption: reward.remainingPointsNeededForRedemption ?? null,
    type: (reward.type as unknown) as LoyaltyEngineReward['type'],
    errors: reward.errors ?? null,
  } as unknown) as LoyaltyEngineReward;
}

export function mapServiceReward(reward: ServiceReward): LoyaltyReward {
  return rewardToLoyaltyReward(mapServiceRewardToReward(reward), reward);
}

export function mapServiceEngineReward(reward: ServiceReward): LoyaltyEngineReward {
  return rewardToEngineReward(mapServiceRewardToReward(reward), reward);
}

function toLegacyRewardCategory(category: ServiceRewardCategory, rewards: LoyaltyReward[]) {
  return {
    __typename: 'RewardCategory',
    _id: category.label,
    label: { __typename: 'LocaleString', en: category.label },
    image: category.image ?? null,
    imageDescription: category.imageDescription
      ? { __typename: 'LocaleString', locale: category.imageDescription }
      : null,
    rewards,
  };
}

export interface IMappedRewardCategories extends IMappedServiceRewardCategories {
  rewardCategories: any[];
  sanityRewardsMap: ISanityRewardsMap;
  engineRewardsMap: IEngineRewardsMap;
}

export function mapServiceRewardCategories(
  serviceCategories: ReadonlyArray<ServiceRewardCategory>
): IMappedRewardCategories {
  const categories: RewardCategory[] = [];
  const rewardCategories: any[] = [];
  const rewardsMap: RewardsMap = {};
  const sanityRewardsMap: ISanityRewardsMap = {};
  const engineRewardsMap: IEngineRewardsMap = {};

  for (const serviceCategory of serviceCategories) {
    const rewards: Reward[] = [];
    const sanityRewards: LoyaltyReward[] = [];

    for (const serviceReward of serviceCategory.rewards) {
      const reward = mapServiceRewardToReward(serviceReward);
      const loyaltyReward = rewardToLoyaltyReward(reward, serviceReward);
      const engineReward = rewardToEngineReward(reward, serviceReward);

      rewards.push(reward);
      sanityRewards.push(loyaltyReward);
      rewardsMap[reward.id] = reward;
      sanityRewardsMap[loyaltyReward._id] = loyaltyReward;
      engineRewardsMap[reward.id] = engineReward;
    }

    categories.push({
      label: serviceCategory.label,
      image: serviceCategory.image ?? undefined,
      imageDescription: serviceCategory.imageDescription ?? undefined,
      rewards,
    });
    rewardCategories.push(toLegacyRewardCategory(serviceCategory, sanityRewards));
  }

  return {
    categories,
    rewards: Object.values(rewardsMap),
    rewardsMap,
    rewardCategories,
    sanityRewardsMap,
    engineRewardsMap,
  };
}

export function isEngineReward(incentive: unknown): incentive is Reward {
  return (
    typeof incentive === 'object' &&
    incentive !== null &&
    !('_id' in incentive) &&
    'id' in incentive &&
    typeof (incentive as { id?: unknown }).id === 'string' &&
    'pointCost' in incentive &&
    typeof (incentive as { pointCost?: unknown }).pointCost === 'number'
  );
}

/** CMS document id. Same value as service `sanityId`; `/rewards/:sanityId` links resolve here. */
export function getRewardDocumentId(reward: Reward): string {
  return reward.cmsId ?? reward.id;
}

/** Resolves engine id or document id. Document id is both `cmsId` and the historic Sanity path id. */
export function findRewardByRouteId(rewardsMap: RewardsMap, routeId: string): Reward | undefined {
  return rewardsMap[routeId] ?? Object.values(rewardsMap).find(reward => reward.cmsId === routeId);
}

export function matchesRewardDetailRoute(
  reward: Reward,
  rewardId: string,
  loyaltyEngineId?: string
): boolean {
  if (loyaltyEngineId && reward.id !== loyaltyEngineId) {
    return false;
  }

  return getRewardDocumentId(reward) === rewardId;
}
